import { useMemo, useState, useDeferredValue, useRef } from 'react';
import { PortfolioTableItemDetails } from '../PortfolioTableItem';
import {
  ZfDialog,
  ZfDialogContent,
  ZfDialogFooter,
  ZfDialogHeader,
  ZfInput,
  ZfStack,
  ZfTextButton,
  ZfBox,
  ZfTypography,
} from '@zafire/react';
import AGGridComponent from '../../../components/AgGrid/AGGridComponent';
import { ColDef } from 'ag-grid-enterprise';

import useGetPortfolioHistory from '../hooks/useGetPortfolioHistory';
import { formatRevisionHistoryClockDate, formatWallClockDateTimeAMPM } from 'utils/dateUtils';
import { RoundDropdown } from '../../../components/common/RoundDropdown';
import { RevisionHistoryDropdown, RevisionHistoryState, RevisionHistoryValue } from './RevisionHistoryDropdown';
import { useTranslation } from 'react-i18next';

type RevisionHistoryProps = { portfolio: PortfolioTableItemDetails };

export type RevisionHistoryRow = {
  dateTimeLabel: string;
  dateTime: Date;
  user: string;
  section: string;
  details: string;
  attribute: string;
  previousValue: string;
  newValue: string;
  id: string;
};

export const RevisionHistory = ({ portfolio }: RevisionHistoryProps) => {
  const { t } = useTranslation();
  const [showDrawer, setShowDrawer] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [cluster, setCluster] = useState<RevisionHistoryValue>(RevisionHistoryState.DATETIME);
  const deferredSearch = useDeferredValue(searchTerm);
  const inputRef = useRef<React.ComponentRef<typeof ZfInput>>(null);

  const { data: historyData, isLoading: isRetrievingHistory } = useGetPortfolioHistory(portfolio.portfolioId);

  const getColumnDefs = useMemo((): ColDef<RevisionHistoryRow>[] => {
    return [
      {
        headerName: 'Date & Time',
        field: 'dateTimeLabel',
        rowGroup: true,
        hide: true,
        flex: 1,
      },
      {
        field: 'user',
        flex: 1,
        cellStyle: { textAlign: 'right', justifyContent: 'flex-end' },
      },

      {
        headerName: 'Section',
        field: 'section',
        flex: 1,
      },
      {
        headerName: 'Attribute',
        field: 'attribute',
        flex: 1,
      },
      { headerName: 'Previous Value', field: 'previousValue', flex: 1 },
      { headerName: 'New Value', field: 'newValue', flex: 1 },
    ];
  }, []);

  const data = useMemo(() => {
    if (historyData && !isRetrievingHistory) {
      const keyword = deferredSearch.toLowerCase();

      const mappedItems = historyData.revisionHistoryItems.map((item, index) => ({
        ...item,
        attribute: t(`pb.revisionhistory.attribute.${item.attribute}`, {
          defaultValue: item.attribute,
        }),
        id: String(index),
      }));

      const filteredItems = mappedItems.filter((item) =>
        Object.values(item).some((value) => String(value).toLowerCase().includes(keyword)),
      );
      // clustering by date collapses every revision of the same day into one group
      const toGroupLabel =
        cluster === RevisionHistoryState.DATE ? formatRevisionHistoryClockDate : formatWallClockDateTimeAMPM;

      return (
        [...filteredItems]
          // groups appear in the order their first row does, so sort newest first before grouping
          .sort((a, b) => b.dateTime.getTime() - a.dateTime.getTime())
          .map((item, index) => ({
            ...item,
            dateTimeLabel: toGroupLabel(item.dateTime),
            id: index,
          }))
      );
    }
    return [];
  }, [cluster, deferredSearch, historyData, isRetrievingHistory, t]);

  return (
    <>
      <ZfTextButton hierarchy="secondary" onClick={() => setShowDrawer(true)}>
        {t('pb.revisionhistory.open')}
      </ZfTextButton>
      <ZfDialog show={showDrawer} size="medium" onDialogClose={() => setShowDrawer(false)}>
        <ZfDialogHeader showCloseButton>Revision history for portfolio {portfolio.portfolioNumber}</ZfDialogHeader>
        <ZfDialogContent>
          <div style={{ boxSizing: 'border-box' }}>
            <ZfBox style={{ flexDirection: 'row', justifyContent: 'space-between', display: 'flex' }}>
              <div style={{ width: '50%', marginBottom: 'var(--zf-spacing-small, 12px)' }}>
                <ZfInput
                  ref={inputRef}
                  data-testid="banking-product-search-input"
                  type="search"
                  clearable
                  showSubmit={false}
                  placeholder="Search in table"
                  onInputChange={(e: CustomEvent<string>) => setSearchTerm(e.detail)}
                  onSearchSubmit={(e: CustomEvent<string>) => setSearchTerm(e.detail)}
                />
              </div>
              <ZfBox
                style={{
                  flexDirection: 'row',
                  gap: 8,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <ZfTypography>{t('pb.revisionhistory.clusterby.label')}</ZfTypography>
                <RoundDropdown
                  options={RevisionHistoryDropdown}
                  value={cluster}
                  hierarchy="primary"
                  onChange={setCluster}
                  size="small"
                />
                <ZfTextButton
                  hierarchy="tertiary"
                  disabled={cluster === RevisionHistoryState.DATETIME}
                  onClick={() => setCluster(RevisionHistoryState.DATETIME)}
                >
                  {t('pb.revisionhistory.resettodefault')}
                </ZfTextButton>
              </ZfBox>
            </ZfBox>
            <div
              style={{
                width: '100%',
              }}
            >
              <AGGridComponent
                id={'revision-history'}
                disableSelection={true}
                domLayout="normal"
                containerStyle={{ height: 300, boxSizing: 'border-box' }}
                rowKey={'id'}
                getColumnDefs={getColumnDefs}
                items={data}
                defaultPopupParent
                groupDisplayType="groupRows"
                groupDefaultExpanded={-1}
                rowSelection={{ mode: 'singleRow', enableClickSelection: false }}
                loading={isRetrievingHistory}
              />
            </div>
          </div>
        </ZfDialogContent>
        <ZfDialogFooter>
          <ZfStack slot="primary" direction="row" spacing="small">
            <ZfTextButton hierarchy="secondary" size="small" onClick={() => setShowDrawer(false)}>
              {t('pb.revisionhistory.close')}
            </ZfTextButton>
          </ZfStack>
        </ZfDialogFooter>
      </ZfDialog>
    </>
  );
};
